import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, X } from "lucide-react";
import { api } from "@/lib/api";

const COLORS = ["#F59E0B", "#EF4444", "#3B82F6", "#22C55E"];
const TEXT_ON = { "#F59E0B": "#0F172A", "#22C55E": "#052E16" };

export function WeekNote({ weekStart, canEdit }) {
  const [note, setNote] = useState({ text: "", color: COLORS[0] });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ text: "", color: COLORS[0] });

  useEffect(() => {
    let alive = true;
    setEditing(false);
    api.get("/week-notes", { params: { week_start: weekStart } })
      .then((r) => { if (alive) setNote(r.data); })
      .catch((err) => console.warn("Weekmelding laden mislukt", err));
    return () => { alive = false; };
  }, [weekStart]);

  const save = async (text) => {
    try {
      const r = await api.put("/week-notes", { week_start: weekStart, text, color: draft.color });
      setNote(r.data);
      setEditing(false);
      toast.success(text.trim() ? "Weekmelding opgeslagen" : "Weekmelding verwijderd");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kon weekmelding niet opslaan"); }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(draft.text); }} className="flex flex-wrap items-center gap-2 mb-2 p-2 border-2 border-slate-900 dark:border-white rounded bg-white dark:bg-slate-800" data-testid="week-note-form">
        <Megaphone size={16} className="text-slate-700 shrink-0"/>
        <input autoFocus maxLength={200} className="pb-input !min-h-[36px] flex-1 min-w-[200px]" placeholder="Bijv. Deze week alleen naloop plannen · Jan op vakantie" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} data-testid="week-note-input"/>
        <div className="flex gap-1">
          {COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setDraft({ ...draft, color: c })} className={`w-6 h-6 rounded border-2 ${draft.color === c ? "border-slate-900 ring-2 ring-orange-400" : "border-slate-300"}`} style={{ background: c }} aria-label={`Kleur ${c}`} data-testid={`week-note-color-${c.slice(1)}`}/>
          ))}
        </div>
        <button type="submit" className="pb-btn-primary !min-h-[36px]" data-testid="week-note-save">Opslaan</button>
        {note.text && <button type="button" className="pb-btn-outline !min-h-[36px]" onClick={() => save("")} data-testid="week-note-delete">Verwijderen</button>}
        <button type="button" onClick={() => setEditing(false)} className="p-1" aria-label="Annuleren" data-testid="week-note-cancel"><X size={16}/></button>
      </form>
    );
  }

  if (!note.text) {
    if (!canEdit) return null;
    return (
      <button type="button" onClick={() => { setDraft({ text: "", color: COLORS[0] }); setEditing(true); }} className="mb-2 px-3 py-1 rounded-full border-2 border-dashed border-orange-500 text-xs font-black text-orange-700 bg-orange-50 hover:bg-orange-100 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-400 dark:hover:bg-orange-900 flex items-center gap-1 transition-colors" data-testid="week-note-add">
        <Plus size={12}/> Weekmelding
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={!canEdit}
      onClick={() => { setDraft({ text: note.text, color: note.color }); setEditing(true); }}
      className="w-full mb-2 px-4 py-2.5 rounded border-2 border-slate-900 dark:border-white text-left text-sm md:text-base font-black flex items-center gap-2 shadow-[3px_3px_0_rgba(15,23,42,0.85)]"
      style={{ background: note.color, color: TEXT_ON[note.color] || "#FFFFFF" }}
      title={canEdit ? "Klik om aan te passen" : undefined}
      data-testid="week-note-banner"
    >
      <Megaphone size={18} className="shrink-0"/> <span className="truncate uppercase tracking-wide">{note.text}</span>
    </button>
  );
}
